import { Component, ChangeDetectionStrategy, inject, input } from '@angular/core';
import { RealEstateService } from '@eadsysteme/ewsrealestateclient-v1';
import { TableColumn } from '../../../@vex/interfaces/table-column.interface';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { RealEstateEedCount, RealEstateEedCountRequest, ServiceTariffInvoiceService, TenantUnitSetting } from '@eadsysteme/energyefficiencyclient-v1';
import { SessionService } from '../../services/session.service';
import { fadeInUp400ms } from '../../../@vex/animations/fade-in-up.animation';
import { stagger40ms } from '../../../@vex/animations/stagger.animation';
import { Icons } from '../../util/Icons';
import { UntypedFormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { LayoutService } from '../../../@vex/services/layout.service';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { SnackbarService } from '../../services/snackbar.service';
import { DateUtil } from '../../util/DateUtil';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { DayjsDateAdapter, MAT_DAYJS_DATE_FORMATS } from '../../util/material-dayjs-adapter';
import {
	MatTableDataSource,
	MatTable,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
	MatNoDataRow,
} from '@angular/material/table';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogContent, MatDialogClose } from '@angular/material/dialog';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { NgClass } from '@angular/common';
import { DefaultClassDirective } from '@ngbracket/ngx-layout/extended';
import { MatFormField, MatLabel, MatInput, MatSuffix, MatError } from '@angular/material/input';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatList, MatListItem } from '@angular/material/list';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import ManualDeliveryOptionEnum = TenantUnitSetting.ManualDeliveryOptionEnum;

dayjs.extend(utc);

@Component({
	selector: 'consumption-information-legacy',
	templateUrl: './consumption-information-legacy.component.html',
	styleUrls: ['./consumption-information-legacy.component.scss'],
	animations: [fadeInUp400ms, stagger40ms],
	providers: [
		{ provide: DateAdapter, useClass: DayjsDateAdapter, deps: [MAT_DATE_LOCALE] },
		{ provide: MAT_DATE_FORMATS, useValue: MAT_DAYJS_DATE_FORMATS },
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		TranslocoDirective,
		CdkScrollable,
		MatDialogContent,
		MatIconButton,
		MatDialogClose,
		MatIcon,
		CdkDrag,
		CdkDragHandle,
		NgClass,
		DefaultClassDirective,
		MatFormField,
		MatLabel,
		MatInput,
		ReactiveFormsModule,
		MatDatepickerInput,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepicker,
		MatError,
		MatProgressSpinner,
		MatList,
		MatListItem,
		MatAccordion,
		MatExpansionPanel,
		MatExpansionPanelHeader,
		MatExpansionPanelTitle,
		MatTable,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatCellDef,
		MatCell,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		MatNoDataRow,
	],
})
export class ConsumptionInformationLegacyComponent {
	currentRealEstateId = inject(MAT_DIALOG_DATA);
	dialogRef = inject<MatDialogRef<ConsumptionInformationLegacyComponent>>(MatDialogRef);
	private translocoService = inject(TranslocoService);
	private serviceTarifInvoice = inject(ServiceTariffInvoiceService);
	private realEstateService = inject(RealEstateService);
	protected sessionService = inject(SessionService);
	private snackbarService = inject(SnackbarService);
	icons = inject(Icons);
	dateUtil = inject(DateUtil);
	layoutService = inject(LayoutService);

	dataSourceTuMonths: MatTableDataSource<TenantUnitEedCountDataSource>;
	dataSourceResidents: MatTableDataSource<ResidentEedCountDataSource>;
	public currentRealEstateEedCount: RealEstateEedCount = {
		totalEed: 0,
		totalEedPrintedByScToPm: 0,
		totalEedPrintedByScToOwner: 0,
		totalEedPrintedByScToResident: 0,
	};
	readonly columns = input<TableColumn<TenantUnitEedCountDataSource>[]>([
		{
			label: this.translate('common.number'),
			property: 'number',
			type: 'text',
			visible: true,
		},
		{
			label: this.translate('common.date'),
			property: 'dueDate',
			type: 'text',
			visible: true,
		},
		{
			label: this.translate('consumptionInformation.totalCreated'),
			property: 'hasEedDate',
			type: 'checkbox',
			visible: true,
		},
		{
			label: this.translate('consumptionInformation.totalBillable'),
			property: 'hasEedBillableDate',
			type: 'checkbox',
			visible: true,
		},
		{
			label: this.translate('consumptionInformation.sentPm'),
			property: 'hasPrintedEedFromScToPm',
			type: 'checkbox',
			visible: true,
		},
		{
			label: this.translate('consumptionInformation.sentResident'),
			property: 'hasPrintedEedFromScToResident',
			type: 'checkbox',
			visible: true,
		},
		{
			label: this.translate('consumptionInformation.sentOwner'),
			property: 'hasPrintedEedFromScToOwner',
			type: 'checkbox',
			visible: true,
		},
	]);
	readonly columnsResident = input<TableColumn<ResidentEedCountDataSource>[]>([
		{
			label: this.translate('common.number'),
			property: 'number',
			type: 'text',
			visible: true,
		},
		{
			label: this.translate('consumptionInformation.resident'),
			property: 'name',
			type: 'text',
			visible: true,
		},
		{
			label: this.translate('consumptionInformation.validFrom'),
			property: 'validFrom',
			type: 'text',
			visible: true,
		},
		{
			label: this.translate('consumptionInformation.validTo'),
			property: 'validTo',
			type: 'text',
			visible: true,
		},
		{
			label: this.translate('consumptionInformation.totalBillable'),
			property: 'totalEedCount',
			type: 'text',
			visible: true,
		},
		{
			label: this.translate('consumptionInformation.totalBillable'),
			property: 'totalEedBillableCount',
			type: 'text',
			visible: true,
		},
		{
			label: this.translate('consumptionInformation.amountSentPmMobile'),
			property: 'printedEedFromScToPm',
			type: 'text',
			visible: true,
		},
		{
			label: this.translate('consumptionInformation.amountSentResidentMobile'),
			property: 'printedEedFromScToResident',
			type: 'text',
			visible: true,
		},
		{
			label: this.translate('consumptionInformation.amountSentOwnerMobile'),
			property: 'printedEedFromScToOwner',
			type: 'text',
			visible: true,
		},
	]);

	periodFrom: UntypedFormControl;
	periodTo: UntypedFormControl;
	showData = false;
	dateError = this.translocoService.translate('errors.date');
	finishedLoading = true;

	constructor() {
		this.periodFrom = new UntypedFormControl(
			dayjs(new Date(2022, 0, 1))
				.utcOffset(0, true)
				.toDate()
		);
		this.periodTo = new UntypedFormControl(dayjs(new Date()).utcOffset(0, true).toDate(), [Validators.required, Validators.minLength(1)]);
	}

	get visibleColumns() {
		return this.columns()
			.filter((column) => column.visible)
			.map((column) => column.property);
	}

	get visibleColumnsResident() {
		return this.columnsResident()
			.filter((column) => column.visible)
			.map((column) => column.property);
	}

	loadData(): void {
		this.showData = false;
		this.finishedLoading = false;
		if (!this.isDateValid()) {
			this.finishedLoading = true;
			return;
		}
		const currentArea = this.sessionService.effectiveArea;
		this.realEstateService.getRealEstateById(currentArea, this.currentRealEstateId).subscribe((realEstate) => {
			const realEstateEedCountRequest: RealEstateEedCountRequest = {
				area: currentArea,
				number: realEstate.number,
				periodFrom: this.dateUtil.getDateWithoutTimezone(this.periodFrom.value).toJSON(),
				periodTo: this.dateUtil.getDateWithoutTimezone(this.periodTo.value).toJSON(),
			};
			this.serviceTarifInvoice.getRealEstateEedCount(currentArea, realEstateEedCountRequest).subscribe({
				next: (value) => {
					this.createTenantunitMonthsDatasource(value);
					this.showData = true;
					this.finishedLoading = true;
				},
				error: () => {
					this.finishedLoading = true;
					this.showData = false;
					this.snackbarService.error(this.translocoService.translate('errors.loading'));
				},
			});
		});
	}

	isDateValid() {
		this.periodTo.markAllAsTouched();
		if (this.periodFrom && this.periodTo) {
			if (dayjs(this.periodTo.value).isAfter(this.periodFrom.value)) {
				return this.periodTo.valid && this.periodFrom.valid;
			}
		}
		this.periodTo.setErrors({ invalid: true });
		return false;
	}

	translate(key: string): string {
		return this.translocoService.translate(key);
	}

	private sortByNumberAndDate(residentCounts: ResidentEedCountDataSource[]) {
		residentCounts.sort((a, b) => {
			if (a.number > b.number) {
				return 1;
			}
			if (a.validFrom > b.validFrom) {
				return 1;
			}
			return 0;
		});
	}

	private createTenantunitMonthsDatasource(value) {
		this.currentRealEstateEedCount = value;
		const tenantUnitEedCounts: TenantUnitEedCountDataSource[] = [];
		const residentCounts: ResidentEedCountDataSource[] = [];

		for (const currentTenantUnitEedCount of this.currentRealEstateEedCount.tenantUnitEedCounts) {
			for (const energyEfficiencyMonth of currentTenantUnitEedCount.energyEfficiencyMonths) {
				const tuEedCountToAdd: TenantUnitEedCountDataSource = {};
				tuEedCountToAdd.number = currentTenantUnitEedCount.number.toString();
				tuEedCountToAdd.subNumber = currentTenantUnitEedCount.subNumber.toString();
				tuEedCountToAdd.dueDate = energyEfficiencyMonth.dueDate;
				tuEedCountToAdd.finished = energyEfficiencyMonth.energyEfficiencyFinished;
				tuEedCountToAdd.billed = energyEfficiencyMonth.energyEfficiencyBillable;
				tuEedCountToAdd.isDeliveredToPropertyManagement = energyEfficiencyMonth.printedEedFromScToPm;
				tuEedCountToAdd.isDeliveredToResident = energyEfficiencyMonth.printedEedFromScToResident;
				tuEedCountToAdd.isDeliveredToOwner = energyEfficiencyMonth.printedEedFromScToOwner;
				tuEedCountToAdd.accessLogId = energyEfficiencyMonth.accessLogId;
				tuEedCountToAdd.selectedDeliveryOption = tuEedCountToAdd.isDeliveredToPropertyManagement
					? 'DELIVERY_TO_PM'
					: tuEedCountToAdd.isDeliveredToResident
						? 'DELIVERY_TO_RESIDENT'
						: tuEedCountToAdd.isDeliveredToOwner
							? 'DELIVERY_TO_OWNER'
							: 'UNKNOWN';
				tenantUnitEedCounts.push(tuEedCountToAdd);
			}
			for (const energyEfficiencyResident of currentTenantUnitEedCount.energyEfficiencyCountsByResident) {
				const residentCount: ResidentEedCountDataSource = {};
				residentCount.number = energyEfficiencyResident.number;
				residentCount.subNumber = energyEfficiencyResident.subNumber;
				residentCount.name = energyEfficiencyResident.name;
				residentCount.validFrom = energyEfficiencyResident.validFrom;
				residentCount.validTo = energyEfficiencyResident.validTo;
				residentCount.totalEedCount = energyEfficiencyResident.eedCountDto.totalEedCreated;
				residentCount.totalEedBillableCount = energyEfficiencyResident.eedCountDto.totalEedBillable;
				residentCount.printedEedFromScToResident = energyEfficiencyResident.eedCountDto.totalEedPrintedByScToResident;
				residentCount.printedEedFromScToOwner = energyEfficiencyResident.eedCountDto.totalEedPrintedByScToOwner;
				residentCount.printedEedFromScToPm = energyEfficiencyResident.eedCountDto.totalEedPrintedByScToPm;
				residentCounts.push(residentCount);
			}
		}
		this.sortByNumberAndDate(residentCounts);
		this.dataSourceTuMonths = new MatTableDataSource<TenantUnitEedCountDataSource>(tenantUnitEedCounts);
		this.dataSourceResidents = new MatTableDataSource<ResidentEedCountDataSource>(residentCounts);
	}
}

class TenantUnitEedCountDataSource {
	number?: string;
	subNumber?: string;
	dueDate?: string;
	finished?: boolean;
	billed?: boolean;
	isDeliveredToPropertyManagement?: boolean;
	isDeliveredToResident?: boolean;
	isDeliveredToOwner?: boolean;
	selectedDeliveryOption?: ManualDeliveryOptionEnum = 'UNKNOWN';
	accessLogId?: string;
}

class ResidentEedCountDataSource {
	number?: number;
	subNumber?: number;
	name?: string;
	validFrom?: string;
	validTo?: string;
	totalEedCount?: number;
	totalEedBillableCount?: number;
	printedEedFromScToPm?: number;
	printedEedFromScToResident?: number;
	printedEedFromScToOwner?: number;
}
